import styled from '@emotion/styled/macro';
import { Variables } from '../base/variables';

const V = Variables.ThemeLight;

/**
 * The expanded row is a recessed surface with white cards on it — the design's way of setting
 * "this payer, in depth" apart from the list around it without changing the console's palette.
 */
export const Panel = styled.div`
    padding: 20px 24px 24px;
    background: ${V.Background1};
`;

export const TopGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: start;
`;

export const Column = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

/** Uppercase label, a rule that fills the remaining width, then an optional right-hand count. */
export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0 10px;

    &:first-of-type {
        margin-top: 0;
    }

    ::after {
        content: '';
        flex: 1;
        height: 1px;
        background: ${V.Border1};
    }
`;

export const SectionTitle = styled.span`
    flex: none;
    font: 600 10px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

/** Sits after the rule, so it needs to come last in the DOM and reset the pseudo-element order. */
export const SectionMeta = styled.span`
    flex: none;
    order: 3;
    font-size: 11px;
    color: ${V.TextMild};
`;

export const SectionAction = styled.span`
    flex: none;
    order: 3;
`;

export const Card = styled.div`
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};

    & + & {
        margin-top: 8px;
    }
`;

export const CardRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

export const CardIcon = styled.span`
    display: inline-flex;
    flex: none;
    color: ${V.TextXMild};
`;

export const CardText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

export const CardTitle = styled.span`
    display: inline-flex;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 500;
    color: ${V.TextStrong};
`;

export const CardSub = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: ${V.TextMild};
`;

export const CardMeta = styled.span`
    flex: none;
    font-size: 11px;
    color: ${V.TextMild};
`;

/** Two columns, matching the design. Nine tiles means the last row holds one. */
export const EdiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 12px;
`;

export const EdiTile = styled.div<{ supported: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 6px;
    background: ${({ supported }) => (supported ? V.PrimaryTranslated1 : V.Background1)};
    border: 1px solid ${({ supported }) => (supported ? V.PrimaryMild1 : V.Border1)};
`;

export const EdiTileText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`;

export const EdiCode = styled.span<{ supported: boolean }>`
    font: 600 10px/1.3 var(--font-mono, monospace);
    letter-spacing: 0.03em;
    color: ${({ supported }) => (supported ? V.TextMedium : V.TextXMild)};
`;

export const EdiLabel = styled.span<{ supported: boolean }>`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 11px;
    color: ${({ supported }) => (supported ? V.TextStrong : V.TextMild)};
`;

export const EdiTileIcon = styled.span<{ supported: boolean }>`
    display: inline-flex;
    flex: none;
    color: ${({ supported }) => (supported ? V.SuccessConstant6 : V.TextXMild)};
`;

export const LaneTable = styled.div`
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};
    overflow: hidden;
`;

export const LaneRow = styled.div<{ header?: boolean }>`
    display: grid;
    grid-template-columns: minmax(0, 1.6fr) 130px 140px minmax(0, 2.4fr);
    gap: 16px;
    align-items: center;
    padding: ${({ header }) => (header ? '10px 16px' : '12px 16px')};

    & + & {
        border-top: 1px solid ${V.Border1};
    }
`;

export const LaneHeadCell = styled.span`
    font: 600 10px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const LaneCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const LaneLabel = styled.span`
    font-size: 13px;
    color: ${V.TextStrong};
`;

export const LaneSub = styled.span`
    font-size: 11px;
    color: ${V.TextMild};
`;

export const Direction = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: ${V.TextMedium};
`;

export const Footer = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 20px;
`;

export const Empty = styled.div`
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px dashed ${V.Border1};
    font-size: 12px;
    color: ${V.TextMild};
    background: ${V.Background3};
`;

/** Category icon inline with a card title, as the design draws it. */
export const InlineIcon = styled.span`
    display: inline-flex;
    align-items: center;
    margin-right: 6px;
    vertical-align: -2px;
    color: ${V.TextMild};
`;

/**
 * ALWAYS PAPER — neutral, not a warning.
 *
 * Reconsiderations going by post is correct behaviour, not a misconfiguration. Colouring it
 * like an alert produces exactly the support ticket it exists to prevent.
 */
export const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 1px 6px;
    border-radius: 4px;
    font: 600 9px/1.6 var(--font-sans, inherit);
    letter-spacing: 0.06em;
    white-space: nowrap;
    color: ${V.TextMedium};
    background: ${V.Background1};
    border: 1px solid ${V.Border1};
`;

/** Holds the ENTER ID chip above the two-column grid. */
export const IdRow = styled.div`
    display: flex;
    margin-bottom: 14px;
`;
